import type { Schema } from '@dealdesk/api-schema';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Popover, PopoverContent, PopoverTrigger, cn, type IconName } from '@/design-system';
import { formatDateTime } from '@/shared/lib/format';

type Summary = Schema<'ResponsibilitySummary'> & Partial<Pick<Schema<'Responsibility'>, 'dueState' | 'dueAt' | 'reason'>>;

const PARTY_ICON: Record<Schema<'ResponsibilityType'>, IconName> = {
  customer: 'concept.customer',
  internal_user: 'concept.internalUser',
  internal_role: 'concept.team',
  bank: 'concept.bank',
  external_party: 'concept.externalParty',
};

export interface PendingWithChipProps {
  pendingWith: Summary | null;
  /** History shown in a popover when the chip is pressed (the feature loads it). */
  history?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * Who the deal is waiting on, and for how long. Party icon + name + age; overdue responsibilities show the age in
 * the negative colour with the word "overdue" (never colour alone). Press for the responsibility history.
 */
export function PendingWithChip({ pendingWith, history, onOpenChange, className }: PendingWithChipProps) {
  const { t } = useTranslation();
  if (!pendingWith) return <span className={cn('text-meta text-ink-muted', className)}>{t('workflow.pendingWith.nobody')}</span>;
  const overdue = pendingWith.dueState === 'breached';
  const dueSoon = pendingWith.dueState === 'due_soon';
  const age = t('workflow.pendingWith.age', { count: pendingWith.ageDays ?? 0 });
  const body = (
    <>
      <Icon name={PARTY_ICON[pendingWith.type]} size="xs" tone="muted" />
      <span className="sr-only">{t('workflow.pendingWith.label')}</span>
      <span className="truncate font-medium text-ink" dir="auto">
        {pendingWith.label}
      </span>
      <span aria-hidden="true" className="text-border-strong">
        ·
      </span>
      <span className={cn('tabular whitespace-nowrap', overdue ? 'font-semibold text-negative' : dueSoon ? 'font-medium text-warning' : 'text-ink-muted')}>
        {age}
        {overdue ? ` · ${t('workflow.pendingWith.overdue')}` : dueSoon ? ` · ${t('workflow.pendingWith.dueSoon')}` : ''}
      </span>
    </>
  );
  const box = cn('inline-flex h-6 max-w-full min-w-0 items-center gap-1.5 rounded-sm border border-border bg-surface px-1.5 text-table', className);
  if (!history) {
    return (
      <span className={box} title={pendingWith.reason ?? undefined}>
        {body}
      </span>
    );
  }
  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(box, 'hover:border-border-strong hover:bg-surface-muted')} aria-label={t('workflow.pendingWith.open', { label: pendingWith.label, age })}>
          {body}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="flex flex-col gap-0.5 border-b border-border px-3 py-2">
          <span className="text-label text-ink-secondary">{t('workflow.pendingWith.historyTitle')}</span>
          {pendingWith.reason ? <span className="text-meta text-ink-secondary" dir="auto">{pendingWith.reason}</span> : null}
          {pendingWith.dueAt ? <span className={cn('text-meta', overdue ? 'text-negative' : 'text-ink-muted')}>{t('records.due', { time: formatDateTime(pendingWith.dueAt) })}</span> : null}
        </div>
        <div className="max-h-80 overflow-y-auto overscroll-contain px-3 py-2">{history}</div>
      </PopoverContent>
    </Popover>
  );
}

/** One line of responsibility history (used inside the chip's popover and on queues). */
export function ResponsibilityHistoryList({ events }: { events: Schema<'ResponsibilityEvent'>[] }) {
  const { t } = useTranslation();
  if (!events.length) return <p className="text-meta text-ink-muted">{t('workflow.pendingWith.noHistory')}</p>;
  return (
    <ol className="flex flex-col">
      {events.map((e) => (
        <li key={e.id} className="flex items-start gap-2 border-b border-border py-1.5 last:border-b-0">
          <Icon name={PARTY_ICON[e.type]} size="xs" tone="muted" className="mt-0.5" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-table text-ink" dir="auto">
              {e.label}
              {e.endedAt === null ? <span className="ms-1.5 text-meta font-medium text-accent">{t('workflow.pendingWith.current')}</span> : null}
            </span>
            <span className="text-meta text-ink-muted">
              {t(`workflow.pendingWith.source.${e.source}`)}
              {e.changedBy ? ` · ${e.changedBy.name}` : ''}
              {e.reason ? ` · ${e.reason}` : ''}
            </span>
          </div>
          <span className="tabular shrink-0 text-meta text-ink-muted">{formatDateTime(e.since)}</span>
        </li>
      ))}
    </ol>
  );
}
